/**
 * ReleaseNotesView Component
 *
 * This component renders the UI for managing release notes.
 */
import React from "react";
import {
  Table,
  Button,
  Input,
  Checkbox,
  Form,
  Modal,
  Pagination,
  Spin,
  Alert,
  DatePicker
} from "antd";
import PropTypes from "prop-types";
import moment from "moment";

const ReleaseNotesView = ({
  notes,
  loading,
  error,
  form,
  onFormChange,
  onSubmit,
  onEdit,
  onDelete,
  editingId,
  onCancelEdit,
  pagination,
  onPageChange,
  modalVisible,
  onModalOk,
  onModalCancel
}) => {
  // Define table columns with appropriate rendering functions
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: "20%"
    },
    {
      title: "Release Notes",
      dataIndex: "note_text",
      key: "note_text",
      width: "50%"
    },
    {
      title: "Published",
      dataIndex: "publish",
      key: "publish",
      width: "10%",
      render: val => (val ? "Yes" : "No") // Convert boolean to Yes/No text
    },
    {
      title: "Published At",
      dataIndex: "published_at",
      key: "published_at",
      width: "10%",
      // Format date as dd-mm-yyyy
      render: val => (val ? moment(val).format("DD-MM-YYYY") : "")
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <>
          <Button
            icon="edit"
            onClick={() => onEdit(record)}
            style={{ marginRight: 8 }}
          />
          <Button icon="delete" danger onClick={() => onDelete(record.id)} />
        </>
      )
    }
  ];

  return (
    <div className="content-container">
      <div className="reputation-wrapper">
        <div className="content-container">
          {/* heading block start */}
          <div className="heading">
            <div className="left">Release Notes</div>
          </div>
          {/* heading block end */}

          {/* Display error message if there's an error */}
          {error && (
            <Alert type="error" message={error} style={{ marginBottom: 16 }} />
          )}

          <div>
            <div className="kc-block white-bg round-borders-4x">
              {/* Form for creating or editing release notes */}
              <Form
                layout="vertical"
                initialValues={form}
                style={{ marginBottom: 24 }}
              >
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[{ required: true, message: "Please enter a title" }]}
                >
                  <Input
                    onChange={e => onFormChange("title", e.target.value)}
                    value={form.title}
                  />
                </Form.Item>
                <Form.Item
                  label="Release notes"
                  name="note_text"
                  rules={[
                    { required: true, message: "Please enter release notes" }
                  ]}
                >
                  <Input.TextArea
                    rows={3}
                    onChange={e => onFormChange("note_text", e.target.value)}
                    value={form.note_text}
                  />
                </Form.Item>
                <Form.Item name="publish" valuePropName="checked">
                  <Checkbox
                    onChange={e => {
                      onFormChange("publish", e.target.checked);
                      // Set today's date if publish is checked and date not set
                      if (e.target.checked && !form.published_at) {
                        onFormChange("published_at", moment());
                      }
                    }}
                    checked={form.publish}
                  >
                    Publish
                  </Checkbox>
                </Form.Item>
                <Form.Item label="Published At" name="published_at">
                  <DatePicker
                    onChange={date => onFormChange("published_at", date)}
                    value={form.published_at ? moment(form.published_at) : null}
                    style={{ width: "30%" }}
                    disabled={!form.publish}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" onClick={onSubmit} loading={loading}>
                    {editingId ? "Update" : "Create"}
                  </Button>
                  {editingId && (
                    <Button style={{ marginLeft: 8 }} onClick={onCancelEdit}>
                      Cancel
                    </Button>
                  )}
                </Form.Item>
              </Form>

              {/* Table displaying release notes with loading indicator */}
              <Spin spinning={loading}>
                <Table
                  dataSource={notes}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  className="kc-table with-box-shadow"
                />
              </Spin>

              {/* Pagination component */}
              {pagination.total > 0 && (
                <div className="pagination-wrapper">
                  <div className="total-results-wrap">
                    <span className="result-count">{pagination.total}</span>
                    {pagination.total > 1 ? " results" : " result"}
                  </div>
                  {pagination.total > pagination.perpage && (
                    <Pagination
                      current={pagination.page}
                      pageSize={pagination.perpage}
                      total={pagination.total}
                      onChange={page => onPageChange(page, pagination.perpage)}
                      showSizeChanger={false}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal for deletion */}
      <Modal
        visible={modalVisible}
        onOk={onModalOk}
        onCancel={onModalCancel}
        okText="Yes"
        cancelText="No"
        title="Delete Release Note"
      >
        Are you sure you want to delete this release note?
      </Modal>
    </div>
  );
};

ReleaseNotesView.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      note_text: PropTypes.string,
      publish: PropTypes.bool,
      created_at: PropTypes.string
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  form: PropTypes.shape({
    title: PropTypes.string,
    note_text: PropTypes.string,
    publish: PropTypes.bool,
    published_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  }).isRequired,
  onFormChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  editingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelEdit: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    perpage: PropTypes.number,
    total: PropTypes.number
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  modalVisible: PropTypes.bool.isRequired,
  onModalOk: PropTypes.func.isRequired,
  onModalCancel: PropTypes.func.isRequired
};

ReleaseNotesView.defaultProps = {
  error: null,
  editingId: null
};

export default ReleaseNotesView;
